import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Dropdown, Button, ButtonGroup, DropdownButton, Collapse, Form } from 'react-bootstrap';
import apiClient from '../api/axios';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { toast } from 'react-toastify';
import ConfirmationModal from '../components/ConfirmationModal';
import CreateFromTemplateModal from '../components/CreateFromTemplateModal';

const CustomToggle = React.forwardRef(({ children, onClick }, ref) => (
    <a href="" ref={ref} onClick={e => { e.preventDefault(); onClick(e); }} className="text-dark p-2">{children}</a>
));

const EventListPage = () => {
    const { isAuthenticated } = useAuth();
    const { canCreate, canDelete, loading: permissionsLoading } = usePermissions('event');
    const navigate = useNavigate();
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const limit = parseInt(process.env.REACT_APP_EVENTS_PER_PAGE, 10) || 10;

    const [showBlankForm, setShowBlankForm] = useState(false);
    const [name, setName] = useState('');
    const [date, setDate] = useState('');
    const [formError, setFormError] = useState('');

    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [eventToDelete, setEventToDelete] = useState(null);
    const [showTemplateModal, setShowTemplateModal] = useState(false);

    const observer = useRef();
    const lastEventElementRef = useCallback(node => {
        if (loading) return;
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore) {
                setPage(prevPage => prevPage + 1);
            }
        });
        if (node) observer.current.observe(node);
    }, [loading, hasMore]);

    const fetchEvents = useCallback(async () => {
        if (!isAuthenticated) { setLoading(false); return; }
        setLoading(true);
        setError('');
        try {
            const skip = (page - 1) * limit;
            const response = await apiClient.get(`/events/all?skip=${skip}&limit=${limit}`);
            setEvents(prevEvents => {
                const existingIds = new Set(prevEvents.map(e => e.id));
                const newEvents = response.data.events.filter(e => !existingIds.has(e.id));
                return [...prevEvents, ...newEvents];
            });
            setHasMore((events.length + response.data.events.length) < response.data.total_count);
        } catch (err) {
            toast.error('Ошибка загрузки событий.');
        } finally {
            setLoading(false);
        }
    }, [page, limit, isAuthenticated, events.length]);

    const refetchEvents = useCallback(() => {
        setPage(1);
        setEvents([]);
        setHasMore(true);
    }, []);

    useEffect(() => {
        refetchEvents();
    }, [isAuthenticated, refetchEvents]);

    useEffect(() => {
        if (isAuthenticated && hasMore) {
            fetchEvents();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, isAuthenticated]);

    const handleCreateEvent = async (e) => {
        e.preventDefault();
        setFormError('');
        try {
            const response = await apiClient.post('/events/', { name, date: new Date(date).toISOString() });
            toast.success('Событие успешно создано!');
            navigate(`/events/${response.data.id}`);
        } catch (err) {
            const errorMessage = err.response?.data?.detail || 'Произошла ошибка при создании события.';
            setFormError(errorMessage);
            toast.error(errorMessage);
        }
    };

    const handleDeleteEvent = (eventId) => { setEventToDelete(eventId); setShowConfirmModal(true); };

    const handleConfirmDelete = async () => {
        if (eventToDelete) {
            try {
                await apiClient.delete(`/events/${eventToDelete}`);
                toast.success('Событие успешно удалено!');
                refetchEvents();
            } catch (err) {
                toast.error('Ошибка удаления события');
            } finally {
                setShowConfirmModal(false);
                setEventToDelete(null);
            }
        }
    };

    if ((loading && page === 1) || permissionsLoading) return <div className="text-center">Загрузка...</div>;
    if (error && !events.length) return <div className="alert alert-danger">{error}</div>;

    return (
        <>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h1 className="h2 mb-0">События</h1>
                {canCreate && (
                    <ButtonGroup>
                        <Button variant="primary" onClick={() => setShowBlankForm(!showBlankForm)} aria-controls="create-event-form" aria-expanded={showBlankForm}>
                            Создать
                        </Button>
                        <DropdownButton as={ButtonGroup} title="" id="bg-nested-dropdown" variant="primary">
                            <Dropdown.Item eventKey="1" onClick={() => setShowTemplateModal(true)}>Создать из шаблона</Dropdown.Item>
                        </DropdownButton>
                    </ButtonGroup>
                )}
            </div>

            <Collapse in={showBlankForm}>
                <div id="create-event-form">
                    <div className="card mb-4">
                        <div className="card-header">Создать новое событие</div>
                        <div className="card-body">
                            <form onSubmit={handleCreateEvent}>
                                <div className="row g-2">
                                    <div className="col-sm"><Form.Control type="text" placeholder="Название события" value={name} onChange={(e) => setName(e.target.value)} required /></div>
                                    <div className="col-sm"><Form.Control type="datetime-local" value={date} onChange={(e) => setDate(e.target.value)} required /></div>
                                    <div className="col-sm-auto"><Button type="submit">Создать</Button></div>
                                </div>
                                {formError && <div className="text-danger mt-2">{formError}</div>}
                            </form>
                        </div>
                    </div>
                </div>
            </Collapse>

            <div className="card mb-4">
                <div className="card-header d-flex justify-content-between align-items-center">
                    <span>Список событий</span>
                </div>
                {events.length > 0 ? (
                    <ul className="list-group list-group-flush">
                        {events.map((event, index) => {
                            const isLastElement = events.length === index + 1;
                            const isPast = new Date(event.date) < new Date();
                            const itemClass = `list-group-item d-flex justify-content-between align-items-center ${isPast ? 'bg-light text-muted' : ''}`;
                            return (
                                <li ref={isLastElement ? lastEventElementRef : null} key={event.id} className={itemClass}>
                                    <div>
                                        <Link to={`/events/${event.id}`} className="fw-bold text-decoration-none">{event.name}</Link>
                                        {event.is_template && <span className="badge bg-info ms-2">Шаблон</span>}
                                        <br />
                                        <small className="text-muted">{new Date(event.date).toLocaleString()}</small>
                                    </div>
                                    {canDelete && (
                                        <Dropdown>
                                            <Dropdown.Toggle as={CustomToggle} id={`dropdown-actions-${event.id}`}>
                                                <i className="fas fa-ellipsis-v"></i>
                                            </Dropdown.Toggle>
                                            <Dropdown.Menu>
                                                <Dropdown.Item onClick={() => handleDeleteEvent(event.id)} className="text-danger">Удалить</Dropdown.Item>
                                            </Dropdown.Menu>
                                        </Dropdown>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                ) : (
                    !loading && (
                        <div className="card-body">
                            <p className="text-center mb-0">Событий пока нет. {canCreate && "Создайте первое."}</p>
                        </div>
                    )
                )}
                {loading && <div className="text-center p-3">Загрузка...</div>}
                {!loading && !hasMore && events.length > 0 && <div className="text-center text-muted p-3">Больше событий нет</div>}
            </div>

            <CreateFromTemplateModal 
                show={showTemplateModal} 
                onHide={() => setShowTemplateModal(false)} 
                onEventCreated={(newEventId) => navigate(`/events/${newEventId}`)} 
            />

            <ConfirmationModal
                show={showConfirmModal}
                title="Подтвердите удаление события"
                message="Вы уверены, что хотите удалить это событие? Это действие необратимо и удалит все связанные данные (участников, пункты плана)."
                onConfirm={handleConfirmDelete}
                onCancel={() => setShowConfirmModal(false)}
                confirmText="Удалить"
                cancelText="Отмена"
            />
        </>
    );
};

export default EventListPage;